import { Banner, Button } from '@nexelem/components';
import { StaticModal, createModal } from '@nexelem/modals';
import { useCustomToast } from '@nexelem/snackbar';
import { useMutation } from '@tanstack/react-query';
import { Trans } from 'react-i18next';
import { useSimulationId } from '../../../../../../components/organisms/ProductionPlans/GanttSimulation/hooks/useSimulationId';
import { useSelectAllStore } from '../../../../../../components/organisms/ToDoList/store/useSelectAllStore';
import { useCustomTranslation } from '../../../../../../hooks';
import { useGraphneAPI } from '@nexelem/graphne-api';
import { useGraphneTaskDetails } from '../../../../../../queryHooks/productionPlanHooks';

type CancelTasksModalProps = {
  onSuccess: () => void;
  checkedTasksId: number[];
  totalTasks: number;
};

export const CancelTasksModal = ({ onSuccess, checkedTasksId, totalTasks }: CancelTasksModalProps) => {
  const { tWrapper, t } = useCustomTranslation('products.productionPlan.cancelTaskModal');
  const graphneApi = useGraphneAPI();
  const { id } = useSimulationId();
  const displayToast = useCustomToast();
  const { data: task } = useGraphneTaskDetails(String(checkedTasksId[0]));

  const selectAll = useSelectAllStore((state) => state.selectAll);
  const setSelectAll = useSelectAllStore((state) => state.setSelectAll);
  const correctCount = selectAll ? totalTasks - checkedTasksId.length : checkedTasksId.length;

  const successMessageTasksCount = tWrapper('successMessage.tasksCount.tasks', { count: correctCount });

  const onSave = useMutation({
    mutationFn: (ids: number[]) =>
      graphneApi.postGraphneSimulationSimulationidTasksCancel({
        simulationCancelTasksView: {
          taskIds: ids,
          selectAll,
        },
        simulationId: String(id),
      }),
    onSuccess: () => {
      closeCancelTasksModal();
      onSuccess();
      setSelectAll(false);
      displayToast({
        toastType: 'success',
        message: (
          <Trans components={{ strong: <strong /> }}>
            {tWrapper('successMessage.message', { tasksCount: successMessageTasksCount })}
          </Trans>
        ),
      });
    },
    onError: () => {
      displayToast({
        toastType: 'error',
        message: tWrapper('errorMessage'),
      });
    },
  });

  const onSubmit = () => {
    onSave.mutate(checkedTasksId);
  };

  return (
    <StaticModal maxWidth="lg" onClose={closeCancelTasksModal}>
      <StaticModal.Header withCloseButton>
        {checkedTasksId.length > 1
          ? tWrapper('header.multipleTasks', { count: checkedTasksId.length })
          : tWrapper('header.oneTask')}
      </StaticModal.Header>
      <StaticModal.Content>
        <div className="flex flex-col gap-6">
          <span className="text-s18">
            <Trans components={{ strong: <strong /> }}>
              {checkedTasksId.length > 1 || selectAll
                ? tWrapper('description.multiple', {
                    count: correctCount,
                  })
                : tWrapper('description.oneTask', { productionLotNumber: task?.name })}
            </Trans>
          </span>
          <Banner emphasis="strong" message={tWrapper('banner')} type="warning" />
        </div>
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button isLoading={onSave.isPending} variant="outlined" onClick={onSubmit}>
          {tWrapper('cancelTaskButton')}
        </Button>
        <Button variant="contained" onClick={closeCancelTasksModal}>
          {t('common.close')}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openCancelTasksModal, closeCancelTasksModal } = createModal(CancelTasksModal, {
  name: 'CancelTasksModal',
});
